import { z } from "zod";
import { CURRENT_POSITION_ELIGIBLE_CLASSES, checkAnswerText, hasInferenceFrame, hasReportingFrame, transientInferenceViolation, } from "./epistemic-selection.js";
/**
 * Staff Subject Ask — structured assertion envelope (ADR-0117 §Staff Ask).
 *
 * When a Subject is pinned, the model returns its answer as a list of
 * assertions, each declaring the epistemic FRAME it speaks in and the records
 * it rests on. Code — not the prompt — then decides whether the cited
 * records' deterministic classes permit that frame, corrects frames it can
 * safely downgrade (a scenario-dependent statement is re-labelled as a
 * scenario; a statement resting on a derived inference is re-labelled as
 * that inference), renders a visible frame label, and withholds whatever
 * claims authority the records do not establish.
 *
 * The model keeps full freedom to synthesise, hypothesise and explore
 * scenarios; it cannot assert organisational knowledge, verification,
 * agreement or another party's position on its own say-so.
 *
 * Authoritative: frame ⇄ record-class validation below. Defence in depth:
 * the prompt contract and the prose guard (`checkAnswerText`) run on the
 * rendered text of every accepted assertion.
 *
 * P2 (derived intelligence): `implication` joins the inference frames; a
 * gap (`no-record`) is kept distinct from a contradiction (`contradicted`,
 * which needs a contradicting record); and inferences Insights made earlier
 * in the same conversation cannot be restated later as established
 * (anti-laundering across turns). Ask answers are never persisted as claims.
 */
export const ASK_ASSERTION_FRAMES = [
    // Authority frames — each requires specific record classes.
    "organisation-position",
    "agreed",
    "verified-observation",
    "counterparty-statement",
    // Retrieval — what the cited records themselves say.
    "reported-record",
    // Inference — reuse the claim model's reasoning modes.
    "synthesis",
    "hypothesis",
    "scenario",
    "implication",
    // Gap detection: the records are silent / insufficient on the point
    // (absence of evidence — never a finding that something is false).
    "no-record",
    // A cited record contradicts the point (or the claim was rejected).
    "contradicted",
];
export const AskAssertionFrameSchema = z.enum(ASK_ASSERTION_FRAMES);
/** Which record classes permit each authority frame. */
const FRAME_REQUIRES = {
    "organisation-position": new Set(["self-position", "agreed"]),
    agreed: new Set(["agreed"]),
    "verified-observation": new Set(["verified-observation"]),
    "counterparty-statement": new Set(["counterparty-position", "agreed"]),
};
const INFERENCE_FRAMES = new Set([
    "synthesis",
    "hypothesis",
    "scenario",
    "implication",
]);
function contradicting(row) {
    return (row.class === "rejected-or-contradicted" ||
        row.evidence.some((ev) => ev.relation === "contradicts"));
}
export const AskAssertionSchema = z.object({
    frame: AskAssertionFrameSchema,
    prose: z.string().trim().min(1).max(4_000),
    citationIds: z.array(z.string().min(1).max(300)).max(20).default([]),
    /** Required for `counterparty-statement`: whose statement it is. */
    attributedTo: z.string().trim().max(200).nullable().default(null),
    /** Required for `scenario`: the condition the consequence depends on. */
    condition: z.string().trim().max(600).nullable().default(null),
});
export const AskAssertionEnvelopeSchema = z.object({
    assertions: z.array(AskAssertionSchema).min(1).max(60),
});
export const SUBJECT_ASK_ASSERTION_CONTRACT = [
    "SUBJECT ASK OUTPUT CONTRACT:",
    "Your FINAL answer must be JSON only (tool calls before it are fine):",
    '{"assertions":[{"frame":"...","prose":"...","citationIds":["..."],"attributedTo":null,"condition":null}]}',
    "Write one assertion per point, in reading order; prose may use markdown. Frames:",
    "organisation-position — the organisation's adopted position/decision (only from records classed self-position or agreed);",
    "agreed — agreement between parties (only from records classed agreed);",
    "verified-observation — a verified fact (only from records classed verified-observation);",
    "counterparty-statement — what a named party itself stated (only from records classed counterparty-position; set attributedTo to that party);",
    "reported-record — what a record, draft or source states (“The draft states …”, “The recorded value is …”); cite the record;",
    "synthesis — “Taken together, A, B and C suggest …”; cite what you combined;",
    "hypothesis — “One possibility worth investigating is …”;",
    "scenario — “If X occurs, Y may follow”; set condition to X;",
    "implication — “If A holds, it would follow that …”; cite the claim(s) or fact(s) it follows from;",
    "no-record — a gap: the records do not answer or establish the point (never say it is false);",
    "contradicted — a cited record contradicts the point, or the claim was rejected; cite that record.",
    "Code checks every frame against the cited records' classes: an authority frame the records do not support is withheld from the user, and anything resting on a scenario or a derived inference is labelled as such. An inference you made earlier in this conversation stays an inference: do not restate it later as established. Make useful connections and inferences freely — just give them an inference frame.",
    "A useful answer usually makes clear, in natural prose rather than fixed headings, what the records establish, what you infer from them, what remains uncertain, and what would change the conclusion (a record that would confirm or contradict it).",
].join(" ");
const LABELLED_INFERENCE = /^\*\*(Synthesis|Hypothesis|Implication|Scenario — if [^*]*?):\*\*\s*([\s\S]+)$/;
const PRIOR_SENTENCE_SPLIT = /(?<=[.!?])\s+/;
/**
 * Collect the inferences Insights made in earlier assistant turns. Subject
 * turns are rendered by `renderSubjectAskAnswer`, whose frame labels are
 * Insights' own deterministic output, so this reads those labels — it does
 * not interpret free prose. Unlabelled prose (prose fallback, pre-envelope
 * turns) contributes its inferentially framed sentences as hypotheses,
 * skipping sentences that report what a record states.
 */
export function conversationInferences(assistantTexts) {
    const out = [];
    for (const text of assistantTexts) {
        for (const paragraph of text.split(/\n{2,}/)) {
            const trimmed = paragraph.trim();
            if (!trimmed || trimmed.startsWith("_Insights withheld"))
                continue;
            const labelled = trimmed.match(LABELLED_INFERENCE);
            if (labelled) {
                const head = labelled[1] ?? "";
                const mode = head.startsWith("Scenario")
                    ? "scenario"
                    : head.toLowerCase();
                for (const sentence of (labelled[2] ?? "").split(PRIOR_SENTENCE_SPLIT)) {
                    if (sentence.trim())
                        out.push({ mode, text: sentence.trim() });
                }
                continue;
            }
            if (trimmed.startsWith("**"))
                continue; // an authority / other framed assertion
            for (const sentence of trimmed.split(PRIOR_SENTENCE_SPLIT)) {
                if (hasInferenceFrame(sentence) && !hasReportingFrame(sentence)) {
                    out.push({ mode: "hypothesis", text: sentence.trim() });
                }
            }
        }
    }
    return out.slice(-200);
}
/** The guard for one turn: the Subject selection plus this conversation's earlier inferences. */
export function withConversationInferences(guard, priorAssistantTexts) {
    return { ...guard, priorInferences: conversationInferences(priorAssistantTexts) };
}
function sameLabel(a, b) {
    return Boolean(a && b && a.trim().toLowerCase() === b.trim().toLowerCase());
}
function label(frame, assertion) {
    switch (frame) {
        case "organisation-position":
            return "Organisation position";
        case "agreed":
            return "Agreed";
        case "verified-observation":
            return "Verified";
        case "counterparty-statement":
            return `${assertion.attributedTo ?? "Counterparty"}, on its own record`;
        case "reported-record":
            return null;
        case "synthesis":
            return "Synthesis";
        case "hypothesis":
            return "Hypothesis";
        case "scenario":
            return `Scenario — if ${(assertion.condition ?? "").replace(/^if\s+/i, "").replace(/[.\s]+$/, "")}`;
        case "implication":
            return "Implication";
        case "no-record":
            return "Not established by the records";
        case "contradicted":
            return "Contradicted by the records";
    }
}
function render(frame, assertion) {
    const lead = label(frame, assertion);
    return lead ? `**${lead}:** ${assertion.prose}` : assertion.prose;
}
/**
 * Deterministic validation of one assertion against the Subject's epistemic
 * selection. Order: ground citations → contain scenarios/inferences → check
 * authority frame against record classes → prose guard on the rendered text.
 */
export function validateAskAssertion(assertion, guard, turnInferences = []) {
    const requestedFrame = assertion.frame;
    const citationIds = assertion.citationIds.filter((id) => guard.allowedCitationIds.has(id));
    const cited = citationIds
        .map((id) => guard.selection.byCitation.get(id))
        .filter((row) => Boolean(row));
    let frame = requestedFrame;
    let effective = { ...assertion, citationIds };
    // Containment: resting on a scenario keeps the scenario's condition.
    const conditional = cited.find((row) => row.conditional || row.class === "scenario");
    if (conditional && frame !== "scenario") {
        frame = "scenario";
        effective = {
            ...effective,
            condition: effective.condition ?? conditional.condition ?? "the scenario's condition holds",
        };
    }
    // Containment: resting on a derived inference stays an inference, and
    // never a stronger one than the weakest inference it rests on.
    const inferences = cited.filter((row) => row.class === "derived-inference");
    const inference = inferences[0];
    if (inference && !INFERENCE_FRAMES.has(frame) && frame !== "no-record") {
        frame =
            inference.inferenceMode === "synthesis" || inference.inferenceMode === "implication"
                ? inference.inferenceMode
                : "hypothesis";
    }
    if ((frame === "synthesis" || frame === "implication") &&
        inferences.some((row) => row.inferenceMode === "hypothesis")) {
        frame = "hypothesis";
    }
    const required = FRAME_REQUIRES[frame];
    if (required) {
        const supporting = cited.filter((row) => required.has(row.class));
        if (supporting.length === 0) {
            return {
                status: "withheld",
                requestedFrame,
                reason: `claims ${frame} but no cited record establishes it`,
            };
        }
        if (frame === "counterparty-statement") {
            const own = supporting.some((row) => (row.class === "counterparty-position" &&
                sameLabel(row.attribution.actorLabel, effective.attributedTo)) ||
                (row.class === "agreed" &&
                    row.externalStances.some((s) => s.evidenced &&
                        s.stance === "agreed" &&
                        sameLabel(s.actorLabel, effective.attributedTo))));
            if (!own) {
                return {
                    status: "withheld",
                    requestedFrame,
                    reason: `attributes a statement to ${effective.attributedTo ?? "a party"} without that party's own record`,
                };
            }
        }
    }
    if ((frame === "reported-record" || frame === "synthesis" || frame === "implication") &&
        citationIds.length === 0) {
        return { status: "withheld", requestedFrame, reason: `${frame} without a cited record` };
    }
    if (frame === "contradicted" && !cited.some(contradicting)) {
        return {
            status: "withheld",
            requestedFrame,
            reason: "claims a contradiction but no cited record contradicts it",
        };
    }
    if (frame === "scenario" && !effective.condition) {
        return { status: "withheld", requestedFrame, reason: "scenario without its condition" };
    }
    const rendered = render(frame, effective);
    const prose = checkAnswerText(rendered, citationIds, guard.selection, {
        inferenceFramed: INFERENCE_FRAMES.has(frame),
    });
    if (!prose.ok) {
        return { status: "withheld", requestedFrame, reason: prose.reason };
    }
    if (frame !== "no-record" && frame !== "contradicted") {
        // Inferences made earlier in this conversation (all frames) and earlier
        // in this answer (non-inference frames) cannot be restated as established.
        const priors = [
            ...(guard.priorInferences ?? []),
            ...(INFERENCE_FRAMES.has(frame) ? [] : turnInferences),
        ];
        const eligible = cited.some((row) => CURRENT_POSITION_ELIGIBLE_CLASSES.has(row.class));
        // An inference frame's visible label covers its whole paragraph; other
        // frames are checked sentence by sentence.
        const units = INFERENCE_FRAMES.has(frame) ? [rendered] : rendered.split(/(?<=[.!?])\s+/);
        for (const unit of units) {
            const laundered = transientInferenceViolation(unit, priors, { eligible });
            if (laundered)
                return { status: "withheld", requestedFrame, reason: laundered };
        }
    }
    return {
        status: "accepted",
        requestedFrame,
        frame,
        citationIds,
        rendered,
        corrected: frame !== requestedFrame,
    };
}
export function parseAskAssertionEnvelope(raw) {
    const trimmed = raw.trim();
    const fenced = trimmed.match(/\{[\s\S]*\}/);
    try {
        return AskAssertionEnvelopeSchema.parse(JSON.parse(fenced?.[0] ?? trimmed));
    }
    catch {
        return null;
    }
}
export const SUBJECT_ASK_WITHHELD_NOTICE = (count) => `_Insights withheld ${count} statement${count === 1 ? "" : "s"} that claimed more than the cited records establish (an unrecorded position, verification, agreement or attribution, or an earlier inference restated as fact). Ask again for the underlying records, or frame it as an inference._`;
const INLINE_CITATION = /\[([a-z][a-z-]*:[^\]\s]+)\]/g;
const FENCE = /^\s*(```|~~~)/;
const TABLE_ROW = /^\s*\|/;
const TABLE_SEPARATOR = /^\s*\|?\s*:?-{2,}/;
const LINE_PREFIX = /^(\s*(?:#{1,6}\s+|[-*+]\s+|\d+[.)]\s+|>\s*)*)([\s\S]*)$/;
const SENTENCE_SPLIT = /(?<=[.!?])\s+(?=[A-Z*_“"([])/;
function checkWithInlineCitations(text, guard) {
    const ids = [...text.matchAll(INLINE_CITATION)]
        .map((match) => match[1] ?? "")
        .filter((id) => guard.allowedCitationIds.has(id));
    const verdict = checkAnswerText(text, ids, guard.selection);
    if (!verdict.ok || !guard.priorInferences?.length)
        return verdict;
    const eligible = ids.some((id) => {
        const entry = guard.selection.byCitation.get(id);
        return entry ? CURRENT_POSITION_ELIGIBLE_CLASSES.has(entry.class) : false;
    });
    const laundered = transientInferenceViolation(text, guard.priorInferences, { eligible });
    return laundered ? { ok: false, reason: laundered } : verdict;
}
/**
 * Deterministic sentence-level guard for free markdown produced in a
 * Subject-pinned turn (the chat prose fallback and Insights AI artefact
 * bodies). Each sentence — heading, list item, paragraph sentence or table
 * row — is checked with the same answer guard using its inline [citation]
 * tokens. Violations are withheld: a table row or list item is dropped
 * whole, a paragraph keeps its other sentences. Fenced code is kept
 * verbatim (it is not prose). Weaker than the structured envelope (no
 * declared frame); strictly stronger than no check.
 */
export function guardSubjectMarkdown(markdown, guard) {
    const out = [];
    const reasons = [];
    let inFence = false;
    for (const line of markdown.split("\n")) {
        if (FENCE.test(line)) {
            inFence = !inFence;
            out.push(line);
            continue;
        }
        if (inFence || line.trim() === "") {
            out.push(line);
            continue;
        }
        if (TABLE_ROW.test(line)) {
            if (TABLE_SEPARATOR.test(line)) {
                out.push(line);
                continue;
            }
            const verdict = checkWithInlineCitations(line, guard);
            if (verdict.ok)
                out.push(line);
            else
                reasons.push(verdict.reason);
            continue;
        }
        const match = line.match(LINE_PREFIX);
        const prefix = match?.[1] ?? "";
        const body = match?.[2] ?? line;
        const kept = [];
        for (const sentence of body.split(SENTENCE_SPLIT)) {
            const verdict = checkWithInlineCitations(sentence, guard);
            if (verdict.ok)
                kept.push(sentence);
            else
                reasons.push(verdict.reason);
        }
        if (kept.length > 0)
            out.push(`${prefix}${kept.join(" ")}`);
    }
    return {
        text: out
            .join("\n")
            .replace(/\n{3,}/g, "\n\n")
            .trim(),
        withheld: reasons.length,
        reasons,
    };
}
/**
 * Prose fallback when the model does not honour the envelope: the whole
 * answer goes through `guardSubjectMarkdown`, with a visible notice for
 * anything withheld.
 */
function renderProseFallback(raw, guard) {
    const guarded = guardSubjectMarkdown(raw, guard);
    const body = guarded.text;
    return {
        text: guarded.withheld > 0
            ? `${body}${body ? "\n\n" : ""}${SUBJECT_ASK_WITHHELD_NOTICE(guarded.withheld)}`
            : body,
        mode: "prose-fallback",
        outcomes: guarded.reasons.map((reason) => ({
            status: "withheld",
            requestedFrame: "reported-record",
            reason,
        })),
        withheld: guarded.withheld,
    };
}
/**
 * Turn the model's raw final answer into the validated text shown to and
 * persisted for the user. Never returns the raw envelope JSON.
 */
export function renderSubjectAskAnswer(raw, guard) {
    const envelope = parseAskAssertionEnvelope(raw);
    if (!envelope)
        return renderProseFallback(raw, guard);
    const turnInferences = envelope.assertions
        .filter((row) => INFERENCE_FRAMES.has(row.frame))
        .map((row) => ({ mode: row.frame, text: row.prose }));
    const outcomes = envelope.assertions.map((assertion) => validateAskAssertion(assertion, guard, turnInferences));
    const accepted = outcomes.filter((row) => row.status === "accepted");
    const withheld = outcomes.length - accepted.length;
    const body = accepted.map((row) => row.rendered).join("\n\n");
    return {
        text: withheld > 0 ? `${body}${body ? "\n\n" : ""}${SUBJECT_ASK_WITHHELD_NOTICE(withheld)}` : body,
        mode: "structured",
        outcomes,
        withheld,
    };
}
/** Merge per-Subject selections when several Subjects are pinned. */
export function mergeEpistemicSelections(selections) {
    const entries = selections.flatMap((row) => row.entries);
    const byCitation = new Map();
    for (const row of selections)
        for (const [id, entry] of row.byCitation)
            byCitation.set(id, entry);
    return {
        entries,
        eligible: entries.filter((entry) => CURRENT_POSITION_ELIGIBLE_CLASSES.has(entry.class)),
        byCitation,
    };
}
//# sourceMappingURL=subject-ask-assertions.js.map